<template>
  <v-dialog :model-value="modelValue" max-width="640" @update:model-value="(v) => emit('update:modelValue', v)">
    <v-card>
      <v-card-title class="text-h6">Not a pharmacy benefit</v-card-title>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mb-4">
          {{ errorMessage }}
        </v-alert>
        <!-- Step 1: confirm the benefit type when OCR could not determine it. -->
        <template v-if="needsBenefitConfirmation">
          <p class="mb-4">
            We could not determine the benefit type for this order from the insurance card. A CoverMyMeds prior
            authorization can only be started for a <strong>pharmacy benefit</strong>. Please confirm:
          </p>
          <div class="d-flex ga-3">
            <v-btn-lh color="primary" @click="emit('confirm-pharmacy')">Pharmacy benefit</v-btn-lh>
            <v-btn-lh color="secondary" @click="confirmedMedical = true">Medical benefit</v-btn-lh>
          </div>
        </template>

        <!-- Step 2: medical-benefit handling options. -->
        <template v-else>
          <p class="mb-4">
            This is a <strong>medical benefit</strong>, so a CoverMyMeds prior authorization cannot be started. Choose
            how to handle it:
          </p>

          <v-radio-group v-model="action" hide-details class="mb-2">
            <v-radio label="Prior authorization is not required" value="pa_not_required"></v-radio>
            <v-radio label="Submit outside our system (track it here)" value="external_medical"></v-radio>
          </v-radio-group>

          <v-expand-transition>
            <div v-if="action === 'external_medical'" class="mt-2">
              <v-text-field
                v-model="form.portal"
                label="Where are you submitting it? (portal name and/or URL)"
                :rules="[(v) => !!v || 'Required']"
                density="comfortable"></v-text-field>
              <v-text-field
                v-model="form.reference"
                label="Reference / confirmation number (optional)"
                density="comfortable"></v-text-field>
              <v-select
                v-model="form.outcome"
                :items="outcomeOptions"
                item-title="label"
                item-value="value"
                label="Outcome"
                density="comfortable"></v-select>
              <v-textarea v-model="form.notes" label="Notes (optional)" rows="2" density="comfortable"></v-textarea>
            </div>
          </v-expand-transition>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn-lh variant="text" :disabled="loading" @click="emit('update:modelValue', false)">Cancel</v-btn-lh>
        <v-btn-lh
          v-if="!needsBenefitConfirmation"
          color="primary"
          :disabled="!canConfirm"
          :loading="loading"
          @click="onConfirm">
          Confirm
        </v-btn-lh>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // "PHARMACY" | "MEDICAL" | null (null when OCR could not determine the benefit type)
  benefitType: { type: String, default: null },
  loading: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
});
const emit = defineEmits(["update:modelValue", "confirm-pharmacy", "mark-pa-not-required", "submit-external"]);

const confirmedMedical = ref(false);
const action = ref("pa_not_required");
const form = ref({ portal: "", reference: "", outcome: "pending", notes: "" });

const outcomeOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Denied", value: "denied" },
];

const needsBenefitConfirmation = computed(() => !props.benefitType && !confirmedMedical.value);

const canConfirm = computed(() => {
  if (action.value === "external_medical") return !!form.value.portal;
  return true;
});

const onConfirm = () => {
  if (action.value === "pa_not_required") {
    emit("mark-pa-not-required");
  } else {
    emit("submit-external", { ...form.value });
  }
};

// Reset transient state each time the dialog is opened.
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      confirmedMedical.value = false;
      action.value = "pa_not_required";
      form.value = { portal: "", reference: "", outcome: "pending", notes: "" };
    }
  }
);
</script>
